import { ERROR_MESSAGES, type ErrorCode } from '@wa/shared';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

/** Access token lives in memory only — never localStorage, so XSS cannot lift it. */
let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string | undefined;

  constructor(init: {
    code: ErrorCode;
    status: number;
    message: string;
    details?: Record<string, unknown>;
    requestId?: string;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details;
    this.requestId = init.requestId;
  }

  /** Friendly text for inline display; falls back to the server's message. */
  get friendlyMessage(): string {
    return ERROR_MESSAGES[this.code] ?? this.message;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Internal: prevents an infinite refresh loop. */
  retryOnUnauthorized?: boolean;
}

async function refreshSession(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE_URL}/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { accessToken: string };
    accessToken = data.accessToken;
    return true;
  } catch {
    return false;
  }
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, retryOnUnauthorized = true } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    credentials: 'include',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
  });

  // One silent refresh, then give up and log the user out.
  if (res.status === 401 && retryOnUnauthorized) {
    if (await refreshSession()) {
      return request<T>(path, { ...options, retryOnUnauthorized: false });
    }
    onUnauthorized?.();
  }

  if (res.status === 204) return undefined as T;

  const payload: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    const envelope = payload as
      | { error?: { code?: ErrorCode; message?: string; details?: Record<string, unknown>; requestId?: string } }
      | null;
    throw new ApiError({
      code: envelope?.error?.code ?? 'INTERNAL',
      status: res.status,
      message: envelope?.error?.message ?? `Request failed (${res.status})`,
      ...(envelope?.error?.details ? { details: envelope.error.details } : {}),
      ...(envelope?.error?.requestId ? { requestId: envelope.error.requestId } : {}),
    });
  }

  return payload as T;
}

/** Build a query string, dropping empty values so URLs stay clean and shareable. */
export function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const v of value) search.append(key, String(v));
    } else {
      search.set(key, String(value));
    }
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}
